import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const DataLayerViewer = ({ isVisible, onClose }) => {
	const [dataLayerWindow, setDataLayerWindow] = useState(null);

	const openDataLayerViewer = useCallback(() => {
		if (dataLayerWindow && !dataLayerWindow.closed) {
			dataLayerWindow.focus();
			return;
		}

		const newWindow = window.open(
			"",
			"dataLayerViewer",
			"width=800,height=600,scrollbars=yes,resizable=yes"
		);

		if (!newWindow) {
			console.warn("Veuillez autoriser les popups pour cette fonctionnalité");
			return;
		}

		setDataLayerWindow(newWindow);

		// Template HTML pour la fenêtre viewer
		newWindow.document.write(`
			<!DOCTYPE html>
			<html lang="fr">
			<head>
				<title>DataLayer Viewer - En temps reel</title>
				<style>
					${getViewerStyles()}
				</style>
			</head>
			<body>
				<div class="header">DataLayer Viewer - Mise à jour en temps réel</div>
				<div class="refresh-info">
					<div class="refresh-info__text">
						Mise à jour automatique toutes les secondes | Tri: Plus récent en haut ↓
					</div>
					<div class="refresh-info__controls">
						<button class="expand-all" onclick="expandAll()">Tout expandre</button>
						<button class="collapse-all" onclick="collapseAll()">Tout collapser</button>
					</div>
				</div>
				<div class="counter" id="eventCounter">Nombre d'événements: 0</div>
				<div class="data-layer" id="dataLayerContainer">
					<div>Chargement...</div>
				</div>
				<script>
					${getViewerScript()}
				</script>
			</body>
			</html>
		`);

		newWindow.document.close();

		// Fonction de mise à jour du contenu
		const updateDataLayer = () => {
			if (newWindow.closed) {
				setDataLayerWindow(null);
				if (onClose) {
					onClose();
				}
				return;
			}

			try {
				const dataLayer = window.dataLayer || [];
				const counter = newWindow.document.getElementById("eventCounter");
				const container = newWindow.document.getElementById("dataLayerContainer");

				if (counter) {
					counter.textContent = `Nombre d'événements: ${dataLayer.length}`;
				}

				if (container && dataLayer.length > 0) {
					// Sauvegarder l'état des événements expandus
					const expandedStates = {};
					const expandedElements = container.querySelectorAll(".event-content.expanded");
					expandedElements.forEach(el => {
						const index = el.id.replace("event-content-", "");
						expandedStates[index] = true;
					});

					// Récupérer le nombre précédent d'événements pour détecter les nouveaux
					const previousCount = parseInt(container.dataset.previousCount || "0", 10);
					const newEventsCount = Math.max(0, dataLayer.length - previousCount);

					// Récupérer quels événements avaient déjà le badge "NOUVEAU"
					const existingNewEvents = new Set();
					const existingNewElements = container.querySelectorAll(".event-item--new");
					existingNewElements.forEach(element => {
						const eventIndex = element.dataset.eventIndex;
						if (eventIndex) {
							existingNewEvents.add(parseInt(eventIndex, 10));
						}
					});

					// Mettre à jour le compteur SEULEMENT s'il y a de nouveaux événements
					if (newEventsCount > 0) {
						container.dataset.previousCount = dataLayer.length;
					}

					// Inverser l'ordre pour avoir le plus récent en haut
					const reversedDataLayer = [...dataLayer].reverse();

					container.innerHTML = reversedDataLayer
						.map((event, reverseIndex) => {
							const originalIndex = dataLayer.length - 1 - reverseIndex;
							const eventType = event.event || "Objet dataLayer";
							const isExpanded = expandedStates[originalIndex.toString()];

							// Un événement doit avoir le badge si :
							// 1. C'est un nouveau événement qui vient d'arriver (newEventsCount > 0 && reverseIndex < newEventsCount)
							// 2. OU il avait déjà le badge et il n'y a pas de nouveaux événements
							const isNewEvent = newEventsCount > 0 && reverseIndex < newEventsCount;
							const hadBadgeAlready = existingNewEvents.has(originalIndex);

							let shouldShowAsNew;
							if (newEventsCount > 0) {
								// S'il y a de nouveaux événements, seuls les nouveaux ont le badge
								shouldShowAsNew = isNewEvent;
							} else {
								// S'il n'y a pas de nouveaux événements, garder les badges existants
								shouldShowAsNew = hadBadgeAlready;
							}

							const newEventClass = shouldShowAsNew ? "event-item--new" : "";

							return `
								<div class="event-item ${newEventClass}" data-event-index="${originalIndex}">
									<div class="event-header" onclick="toggleEvent(${originalIndex})">
										<div>
											<span class="event-index">#${originalIndex + 1}</span>
											${shouldShowAsNew ? '<span class="new-badge">NOUVEAU</span>' : ""}
											- <span class="event-type">${eventType}</span>
										</div>
										<span class="expand-icon" id="expand-icon-${originalIndex}">${isExpanded ? "▼" : "▶"}</span>
									</div>
									<div class="event-content ${isExpanded ? "expanded" : ""}" id="event-content-${originalIndex}">
										<pre>${safeStringify(event)}</pre>
									</div>
								</div>
							`;
						})
						.join("");
				} else if (container && dataLayer.length === 0) {
					container.innerHTML = "<div>Aucun événement dans le dataLayer</div>";
					container.dataset.previousCount = "0";
				}
			} catch (error) {
				console.error("Erreur lors de la mise à jour du dataLayer:", error);
			}
		};

		// Mise à jour initiale
		updateDataLayer();

		// Mise à jour périodique
		const interval = setInterval(updateDataLayer, 1000);

		// Nettoyage quand la fenêtre est fermée
		newWindow.addEventListener("beforeunload", () => {
			clearInterval(interval);
			setDataLayerWindow(null);
			if (onClose) {
				onClose();
			}
		});
	}, [dataLayerWindow, onClose]);

	// Nettoyage lors du démontage du composant
	useEffect(() => {
		return () => {
			if (dataLayerWindow && !dataLayerWindow.closed) {
				dataLayerWindow.close();
			}
		};
	}, [dataLayerWindow]);

	// Auto-ouverture si isVisible devient true
	useEffect(() => {
		if (isVisible) {
			openDataLayerViewer();
		}
	}, [isVisible, openDataLayerViewer]);

	return {
		openViewer: openDataLayerViewer,
		closeViewer: () => {
			if (dataLayerWindow && !dataLayerWindow.closed) {
				dataLayerWindow.close();
				setDataLayerWindow(null);
				if (onClose) {
					onClose();
				}
			}
		},
		isViewerOpen: Boolean(dataLayerWindow && !dataLayerWindow.closed),
	};
};

// Fonction pour obtenir les styles CSS du viewer
const getViewerStyles = () => `
	body { 
		font-family: Arial, sans-serif; 
		margin: 20px; 
		background: #f5f5f5; 
	}
	.header { 
		background: #333; 
		color: white; 
		padding: 15px; 
		margin: -20px -20px 20px -20px; 
		font-size: 18px; 
		font-weight: bold;
	}
	.counter {
		background: #2196F3;
		color: white;
		padding: 10px;
		border-radius: 5px;
		margin-bottom: 15px;
		font-weight: bold;
	}
	.data-layer { 
		background: white; 
		border: 1px solid #ddd; 
		border-radius: 5px; 
		padding: 15px; 
		max-height: 500px; 
		overflow-y: auto; 
	}
	.event-item {
		border: 1px solid #e0e0e0;
		border-radius: 4px;
		margin-bottom: 10px;
		background: #fafafa;
		transition: all 0.3s ease;
	}
	.event-item--new {
		background: linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%);
		border: 2px solid #4CAF50;
		box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
	}
	@keyframes newEventPulse {
		0%, 100% { transform: scale(1); }
		50% { transform: scale(1.02); }
	}
	.new-badge {
		background: #4CAF50;
		color: white;
		font-size: 10px;
		font-weight: bold;
		padding: 2px 6px;
		border-radius: 3px;
		margin-left: 8px;
	}
	@keyframes badgeFadeIn {
		from { opacity: 0; transform: scale(0.8); }
		to { opacity: 1; transform: scale(1); }
	}
	.event-header {
		padding: 10px;
		background: #f0f0f0;
		cursor: pointer;
		border-bottom: 1px solid #e0e0e0;
		display: flex;
		justify-content: space-between;
		align-items: center;
		user-select: none;
	}
	.event-header:hover {
		background: #e8e8e8;
	}
	.event-index {
		font-weight: bold;
		color: #666;
		margin-right: 5px;
	}
	.event-order {
		font-size: 10px;
		color: #999;
		margin-left: 5px;
	}
	.event-type {
		font-weight: bold;
		color: #2196F3;
	}
	.expand-icon {
		font-weight: bold;
		color: #666;
	}
	.event-content {
		padding: 15px;
		display: none;
		border-top: 1px solid #e0e0e0;
	}
	.event-content.expanded {
		display: block;
	}
	pre { 
		margin: 0; 
		white-space: pre-wrap; 
		font-size: 12px; 
		background: white;
		padding: 10px;
		border-radius: 3px;
		border: 1px solid #ddd;
	}
	.refresh-info {
		color: #666;
		font-size: 12px;
		margin-bottom: 10px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		gap: 10px;
	}
	.refresh-info__text {
		flex: 1;
		min-width: 200px;
	}
	.refresh-info__controls {
		display: flex;
		gap: 8px;
	}
	.expand-all {
		background: #4CAF50;
		color: white;
		border: none;
		padding: 8px 15px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 12px;
		transition: background 0.3s ease;
	}
	.expand-all:hover {
		background: #45a049;
	}
	.collapse-all {
		background: #FF9800;
		color: white;
		border: none;
		padding: 8px 15px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 12px;
		transition: background 0.3s ease;
	}
	.collapse-all:hover {
		background: #f57c00;
	}
`;

// Fonction pour obtenir le script JavaScript du viewer
const getViewerScript = () => `
	function toggleEvent(index) {
		const content = document.getElementById('event-content-' + index);
		const icon = document.getElementById('expand-icon-' + index);
		if (content.classList.contains('expanded')) {
			content.classList.remove('expanded');
			icon.textContent = '▶';
		} else {
			content.classList.add('expanded');
			icon.textContent = '▼';
		}
	}
	
	function expandAll() {
		const contents = document.querySelectorAll('.event-content');
		const icons = document.querySelectorAll('.expand-icon');
		contents.forEach(content => content.classList.add('expanded'));
		icons.forEach(icon => icon.textContent = '▼');
	}
	
	function collapseAll() {
		const contents = document.querySelectorAll('.event-content');
		const icons = document.querySelectorAll('.expand-icon');
		contents.forEach(content => content.classList.remove('expanded'));
		icons.forEach(icon => icon.textContent = '▶');
	}
`;

const safeStringify = obj => {
	try {
		const cache = new Set();
		return JSON.stringify(
			obj,
			(key, value) => {
				if (typeof value === "object" && value !== null) {
					if (cache.has(value)) {
						return "[Circular]";
					}
					cache.add(value);
				}
				return value;
			},
			2
		);
	} catch (error) {
		return "Impossible d'afficher l'événement";
	}
};

DataLayerViewer.propTypes = {
	isVisible: PropTypes.bool,
	onClose: PropTypes.func,
};

DataLayerViewer.defaultProps = {
	isVisible: false,
	onClose: null,
};

export default DataLayerViewer;
